import React, { useCallback, useMemo } from 'react';

import {
  Image,
  ImageResizeOptions,
  DotNavigation,
  IconButton,
  IconButtonThemes,
} from 'wix-ui-tpa';

import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common/on-stage';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';

import { useRootProps } from 'AiAssistantWidget/Widget/contexts';
import stylesParams from 'AiAssistantWidget/stylesParams';

import { ImageBlock } from '../ImageBlock';

import { st, classes, cssStates } from './ProductGallery.st.css';

type ProductGalleryProps = {
  images: string[]; // Make it product item to get name for alt
  isSmall?: boolean;
};

// TODO: figure out a better way
const stepLarge = 320 - 24; // Size minus padding
const stepSmall = 250 - 24;
const stepMobile = 215 - 24;

export const ProductGallery: React.FC<
  ProductGalleryProps & React.HTMLAttributes<HTMLElement>
> = ({ images, isSmall, ...rootElementAttrs }) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { direction } = useRootProps();
  const isRTL = direction === 'rtl';
  const [currentIndex, setCurrentIndex] = React.useState(
    // Math.round(((images.length - 1) / 2) | 0), // Array should have at least one item
    0, // Array should have at least one item
  );
  const styles = useStyles();
  const step =
    (isMobile ? stepMobile : isSmall ? stepSmall : stepLarge) -
    (styles.get(stylesParams.messagesCardBorderWidth) ?? 0) * 2;
  const isMultiple = useMemo(() => images.length > 1, [images]);

  const marginInlineStart = useMemo(() => {
    if (!isMultiple || currentIndex === 0) {
      return 0;
    }
    const value = currentIndex * step;
    return (isRTL ? value : -value) + 'px';
  }, [currentIndex, step, isMultiple, isRTL]);

  const showPreviousArrow = useMemo(
    () => isMultiple && currentIndex > 0,
    [currentIndex, isMultiple],
  );
  const showNextArrow = useMemo(
    () => isMultiple && currentIndex < images.length - 1,
    [currentIndex, images, isMultiple],
  );

  const onClickArrow = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      switch (e.currentTarget.name) {
        case 'previous': {
          setCurrentIndex((prev) => Math.max(0, prev - 1));
          break;
        }
        case 'next': {
          setCurrentIndex((prev) => Math.min(images.length - 1, prev + 1));
          break;
        }
      }
    },
    [images.length],
  );

  return (
    <div
      {...rootElementAttrs}
      className={st(classes.root, rootElementAttrs.className)}
      style={{
        ...rootElementAttrs.style,
        maxWidth: `${step}px`,
      }}
    >
      <div className={st(classes.scroller)} style={{ marginInlineStart }}>
        {images.map((url, i) => (
          <ImageBlock
            className={st(classes.imageBlock)}
            aspectRatio={styles.get(stylesParams.productImageAspectRatio)}
          >
            <Image
              className={st(classes.image)}
              key={i}
              src={url}
              resize={ImageResizeOptions.cover}
              width={226}
              aspectRatio={styles.get(stylesParams.productImageAspectRatio)}
            />
          </ImageBlock>
        ))}
      </div>
      {isMultiple && (
        <IconButton
          className={st(
            classes.arrow,
            classes.arrowLeft,
            cssStates({ visible: Boolean(showPreviousArrow) }),
          )}
          theme={IconButtonThemes.Box}
          icon={isRTL ? <ChevronRight /> : <ChevronLeft />}
          name="previous"
          aria-label={t('app.widget.ariallabel.previousImage')}
          tabIndex={-1}
          onClick={onClickArrow}
        />
      )}
      {isMultiple && (
        <IconButton
          className={st(
            classes.arrow,
            classes.arrowRight,
            cssStates({ visible: Boolean(showNextArrow) }),
          )}
          theme={IconButtonThemes.Box}
          icon={isRTL ? <ChevronLeft /> : <ChevronRight />}
          name="next"
          aria-label={t('app.widget.ariallabel.nextImage')}
          tabIndex={-1}
          onClick={onClickArrow}
        />
      )}
      {isMultiple && (
        <DotNavigation
          className={st(classes.navigation)}
          length={images.length}
          showBorder
          currentIndex={currentIndex}
          onSelect={setCurrentIndex}
        />
      )}
    </div>
  );
};
